import { leadInPlainText } from "@/lib/lead-in-html"
import type { LeoQuestionDraftPatch } from "@/lib/library-authoring"

export const BLOOD_PRESSURE_EXERCISE_STEM =
  "In a healthy adult during moderate aerobic exercise, which blood pressure response is expected?"

export const BLOOD_PRESSURE_EXERCISE_CHOICES = [
  "Systolic pressure rises, while diastolic pressure remains stable",
  "Systolic pressure rises, while diastolic pressure rises significantly",
  "Systolic pressure falls, while diastolic pressure remains stable",
  "Systolic pressure falls, while diastolic pressure rises significantly",
] as const

export const VITAL_SIGN_EXERCISE_STEM =
  "Which vital sign change is expected in a healthy adult during moderate exercise?"

export const VITAL_SIGN_EXERCISE_CHOICES = [
  "Both systolic and diastolic fall",
  "Systolic falls, diastolic rises",
  "Systolic pressure rises, diastolic stays near baseline",
  "Neither changes",
] as const

/** B and D. A and the keyed choice stay unmarked. */
export const VITAL_SIGN_EXERCISE_MARKED_CHOICES = [
  VITAL_SIGN_EXERCISE_CHOICES[1],
  VITAL_SIGN_EXERCISE_CHOICES[3],
] as const

export function matchesVitalSignExerciseChoices(
  options: ReadonlyArray<{ text: string }>,
): boolean {
  return VITAL_SIGN_EXERCISE_CHOICES.every(
    (text, index) => options[index]?.text.trim() === text,
  )
}

export type VitalSignVariationSnapshot = {
  leadIn: string
  options: Array<{
    id: string
    text: string
    isCorrect: boolean
    rationale: string
  }>
}

const variationUndoByItemId = new Map<string, VitalSignVariationSnapshot>()

export function rememberVitalSignVariationUndo(
  itemId: string,
  snapshot: VitalSignVariationSnapshot,
) {
  variationUndoByItemId.set(itemId, snapshot)
}

export function peekVitalSignVariationUndo(
  itemId: string,
): VitalSignVariationSnapshot | null {
  return variationUndoByItemId.get(itemId) ?? null
}

export function takeVitalSignVariationUndo(
  itemId: string,
): VitalSignVariationSnapshot | null {
  const snapshot = variationUndoByItemId.get(itemId) ?? null
  variationUndoByItemId.delete(itemId)
  return snapshot
}

export function vitalSignExerciseUndoPatch(
  itemId: string,
  currentOptions: VitalSignVariationSnapshot["options"],
): LeoQuestionDraftPatch | null {
  const remembered = takeVitalSignVariationUndo(itemId)
  if (remembered) return remembered
  return {
    leadIn: VITAL_SIGN_EXERCISE_STEM,
    options: VITAL_SIGN_EXERCISE_CHOICES.map((text, index) => {
      const current = currentOptions[index]
      return {
        id: current?.id ?? String.fromCharCode(97 + index),
        text,
        isCorrect: index === 2,
        rationale: current?.rationale ?? "",
      }
    }),
  }
}

export function isVitalSignExerciseStem(stem: string): boolean {
  const text = leadInPlainText(stem)
  return /\bvital sign\b/i.test(text) && /\bexercise\b/i.test(text)
}

export function isBloodPressureExerciseStem(stem: string): boolean {
  return (
    leadInPlainText(stem).trim().toLowerCase() ===
    BLOOD_PRESSURE_EXERCISE_STEM.toLowerCase()
  )
}

function choicesMatchBloodPressureVariation(
  options: ReadonlyArray<{ text: string; isCorrect: boolean }>,
): boolean {
  return (
    BLOOD_PRESSURE_EXERCISE_CHOICES.every(
      (text, index) => options[index]?.text.trim() === text,
    ) && options[0]?.isCorrect === true
  )
}

export function isVitalSignExerciseVariationApplied(input: {
  leadIn: string
  options: ReadonlyArray<{ text: string; isCorrect: boolean }>
}): boolean {
  return (
    isBloodPressureExerciseStem(input.leadIn) &&
    choicesMatchBloodPressureVariation(input.options)
  )
}

/** Full stem + choice rewrite for Create variations on the vital-sign item. */
export function vitalSignExerciseVariationPatch(input: {
  leadIn: string
  options: ReadonlyArray<{
    id: string
    text: string
    isCorrect: boolean
    rationale: string
  }>
}): LeoQuestionDraftPatch | null {
  const stem = leadInPlainText(input.leadIn)
  const onThisItem =
    isVitalSignExerciseStem(stem) || isBloodPressureExerciseStem(stem)
  if (!onThisItem) return null
  if (isVitalSignExerciseVariationApplied({ leadIn: stem, options: input.options })) {
    return null
  }

  return {
    leadIn: BLOOD_PRESSURE_EXERCISE_STEM,
    options: BLOOD_PRESSURE_EXERCISE_CHOICES.map((text, index) => {
      const current = input.options[index]
      return {
        id: current?.id ?? String.fromCharCode(97 + index),
        text,
        isCorrect: index === 0,
        rationale: current?.rationale ?? "",
      }
    }),
  }
}
